import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  KnowledgeBaseEntry,
  KnowledgeBaseInsert,
} from '@/services/chatbot-config'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form'
import { Loader2 } from 'lucide-react'

const formSchema = z.object({
  trigger_pattern: z.string().min(1, 'Trigger pattern is required'),
  response_text: z.string().min(1, 'Response is required'),
  is_regex: z.boolean().default(false),
  is_active: z.boolean().default(true),
})

interface KnowledgeBaseDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialData?: KnowledgeBaseEntry | null
  initialKeywords?: string
  onSubmit: (data: KnowledgeBaseInsert) => Promise<void>
  isSubmitting: boolean
}

export const KnowledgeBaseDialog = ({
  open,
  onOpenChange,
  initialData,
  initialKeywords,
  onSubmit,
  isSubmitting,
}: KnowledgeBaseDialogProps) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      trigger_pattern: '',
      response_text: '',
      is_regex: false,
      is_active: true,
    },
  })

  useEffect(() => {
    if (initialData) {
      form.reset({
        trigger_pattern:
          initialData.trigger_pattern ||
          (initialData.keywords ? initialData.keywords.join(', ') : ''),
        response_text: initialData.response_text,
        is_regex: initialData.is_regex || false,
        is_active: initialData.is_active,
      })
    } else {
      form.reset({
        trigger_pattern: initialKeywords || '',
        response_text: '',
        is_regex: false,
        is_active: true,
      })
    }
  }, [initialData, initialKeywords, form, open])

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    // Helper: keep keywords array synced for legacy compat if needed,
    // but we primarily use trigger_pattern now.
    const keywordsArray = values.is_regex
      ? []
      : values.trigger_pattern
          .split(',')
          .map((k) => k.trim())
          .filter((k) => k.length > 0)

    await onSubmit({
      trigger_pattern: values.trigger_pattern,
      keywords: keywordsArray,
      response_text: values.response_text,
      is_regex: values.is_regex,
      is_active: values.is_active,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {initialData
              ? 'Edit Knowledge Base Entry'
              : 'Add Knowledge Base Entry'}
          </DialogTitle>
          <DialogDescription>
            Define triggers and the corresponding bot response.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="is_regex"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Use Regex</FormLabel>
                    <FormDescription>
                      Enable advanced pattern matching with Regular Expressions.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="trigger_pattern"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {form.watch('is_regex') ? 'Regex Pattern' : 'Keywords'}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={
                        form.watch('is_regex')
                          ? 'e.g. ^(hi|hello).*'
                          : 'e.g. price, cost, how much...'
                      }
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {form.watch('is_regex')
                      ? 'Valid Regular Expression (Case insensitive by default).'
                      : 'Comma-separated list of trigger words or phrases.'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="response_text"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Response</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="The bot's answer..."
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Active</FormLabel>
                    <FormDescription>
                      Enable or disable this response.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
